import React, { useState } from 'react';
import './PlayerInput.css';
import { Player } from '../types';
import { storage } from '../utils/storage';

interface PlayerInputProps {
  players: Player[];
  setPlayers: (players: Player[]) => void;
}

const PlayerInput: React.FC<PlayerInputProps> = ({ players, setPlayers }) => {
  const [inputValue, setInputValue] = useState('');
  const [showPlayerList, setShowPlayerList] = useState(false);

  const capitalizeName = (name: string) => {
    return name.trim().charAt(0).toUpperCase() + name.trim().slice(1).toLowerCase();
  };

  const addPlayers = () => {
    if (!inputValue.trim()) return;

    // Check if input contains commas
    if (inputValue.includes(',')) {
      // Multiple players
      const names = inputValue
        .split(',')
        .map(name => capitalizeName(name))
        .filter(name => name.length > 0);

      const newPlayers: Player[] = names.map(name => ({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        name: name
      }));

      setPlayers([...players, ...newPlayers]);
    } else {
      // Single player
      const newPlayer: Player = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        name: capitalizeName(inputValue)
      };

      setPlayers([...players, newPlayer]);
    }

    setInputValue('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      addPlayers();
    }
  };

  const removePlayer = (id: string) => {
    setPlayers(players.filter(player => player.id !== id));
  };

  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all data? This will remove all players and reset your settings.')) {
      // Clear players
      setPlayers([]);
      
      // Clear localStorage using the utility
      storage.clearAll();
    }
  };

  return (
    <div className="player-input-container">
      <div className="input-section">
        <h3 className="section-title">Add Players</h3>
        <p className="section-description">
          Enter a single name or multiple names separated by commas
        </p>
        <div className="input-group">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="e.g., Ivan, Player2, Player3"
            className="smart-input"
          />
          <button 
            onClick={addPlayers}
            className="add-button"
            disabled={!inputValue.trim()}
          >
            Add
          </button>
        </div>
      </div>

      {players.length > 0 && (
        <div className="player-list-section">
          <button 
            onClick={() => setShowPlayerList(!showPlayerList)}
            className="toggle-list-button"
          >
            {showPlayerList ? 'Hide' : 'Show'} Player List ({players.length})
          </button>
          
          {showPlayerList && (
            <div className="collapsible-player-list">
              <div className="list-header">
                <h4 className="list-title">Players</h4>
                <div className="list-actions">
                  <button 
                    onClick={clearAllData}
                    className="clear-all-data-button"
                  >
                    Clear All Data
                  </button>
                </div>
              </div>
              
              <div className="players-grid">
                {players.map((player) => (
                  <div key={player.id} className="player-card">
                    <span className="player-name">{player.name}</span>
                    <button 
                      onClick={() => removePlayer(player.id)}
                      className="remove-button"
                      aria-label={`Remove ${player.name}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayerInput;
